import { AfterViewInit, DestroyRef, Directive, ElementRef, inject } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { onResize } from '@coin/shared/core/util-helpers';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { animationFrames, debounce, startWith } from 'rxjs';

/** Enables or disables matTooltip for if the attached element has ellipsis overflow and sets the content to the text content.
 * While coinEllipsisTooltip is meant for single elements, coinChildrenEllipsisTooltip can be used if several elements needs to be displayed.
 * @example `<p class="overflow-ellipsis" coinChildrenEllipsisTooltip matTooltip> Hello World! </p>`
 * */
@Directive({
  selector: '[coinChildrenEllipsisTooltip][matTooltip]',
  standalone: true
})
export class EllipsisChildenTooltipDirective implements AfterViewInit {
  private matTooltip = inject(MatTooltip);
  private elem = inject<ElementRef<HTMLElement>>(ElementRef);
  private destroyRef = inject(DestroyRef);

  public ngAfterViewInit(): void {
    onResize(this.elem.nativeElement)
      .pipe(
        startWith(null),
        debounce(() => animationFrames()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        const element = this.elem.nativeElement;
        const children = Array.from(element.querySelectorAll<HTMLElement>('p'));

        const isAnyEllipsed = children.some(isEllipsisActive);

        this.matTooltip.disabled = !isAnyEllipsed;
        this.matTooltip.message = children
          .map(child => child.textContent?.trim())
          .filter(Boolean)
          .join('\n');
      });
  }
}

function isEllipsisActive(element: HTMLElement): boolean {
  return element.offsetWidth < element.scrollWidth || element.offsetHeight < element.scrollHeight;
}
